import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  FilterGroup,
  FilterGroupPrivileges,
} from 'data/filterGroup/filterGroup.types';
import * as actions from 'data/filterGroup/filterGroup.actions';
import * as selectors from 'data/filterGroup/filterGroup.selectors';
import { AppToaster } from 'shared/toaster';
import FilterGroupGrid from './components/FilterGroupGrid';
import { filterGroupColumnDefs } from '../../FilterGroup.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  data: selectors.getFilterGroupList(state),
  selectedFilterGroup: selectors.getSelectedFilterGroup(state),
});

const mapDispatch = {
  goToFilterGroupCreate: actions.goToFilterGroupCreate,
  goToFilterGroupEdit: actions.goToFilterGroupEdit,
  goToFilterGroupView: actions.goToFilterGroupView,
  selectFilterGroup: actions.selectFilterGroup,
  getFilterGroupList: actions.getFilterGroupList,
  deleteFilterGroup: actions.deleteFilterGroup,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const FilterGroupList: FunctionComponent<Props> = ({
  history,
  beforeLogout,
  privileges,
  data,
  selectedFilterGroup,
  goToFilterGroupCreate,
  goToFilterGroupEdit,
  goToFilterGroupView,
  selectFilterGroup,
  getFilterGroupList,
  deleteFilterGroup,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(filterGroupColumnDefs);

  useEffect(() => {
    getFilterGroupList();
    return () => {
      AppToaster.clear();
    };
  }, [getFilterGroupList]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getFilterGroupList();

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToFilterGroupCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectFilterGroup(event.api.getSelectedRows());
  };

  const handleDeleteFilterGroup = () => {
    const filterGroupId = selectedFilterGroup[0]?.id;
    if (filterGroupId == null) {
      return;
    }

    const selectedFilterGroupName = selectedFilterGroup[0]?.name || '';
    deleteFilterGroup(filterGroupId, selectedFilterGroupName, history);
  };

  const handleGoToEdit = () => goToFilterGroupEdit(history);

  const handleGoToView = () => goToFilterGroupView(history);

  const hasPrivilegeToView = privileges.includes(FilterGroupPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(FilterGroupPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(FilterGroupPrivileges.Delete);

  return (
    <Fragment>
      <FilterGroupGrid
        data={data}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedFilterGroup={selectedFilterGroup[0]}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteFilterGroup}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
    </Fragment>
  );
};

export default withRouter(connector(FilterGroupList));
